import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { useLenis } from "../hooks/useLenis";
import { useSEO } from "../hooks/useSEO";
import { api } from "../lib/api";
import { scrollToSectionId } from "../lib/scroll";
import { Navbar } from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import { WhatsAppFloat } from "../components/layout/WhatsAppFloat";
import { Hero } from "../components/sections/Hero";
import { Services } from "../components/sections/Services";
import { Gallery } from "../components/sections/Gallery";
import { Videos } from "../components/sections/Videos";
import { QuoteCalculator } from "../components/sections/QuoteCalculator";
import { Contact } from "../components/sections/Contact";

const SERVICE_TO_PACKAGE_CATEGORY = {
  boda: "bodas", xv: "xv", bautizo: "bautizo", sesion: "sesion",
  foto_video: "foto_video", save_the_date: "save_the_date", invitacion_digital: "invitacion_digital",
};

const MARQUEE_ITEMS = Array.from({ length: 6 }, (_, i) => `marquee-item-${i}`);

export default function Home() {
  useLenis();
  const location = useLocation();
  const [content, setContent] = useState(null);
  const [prices, setPrices] = useState(null);
  const [presetCategory, setPresetCategory] = useState(
    location.state?.presetCategory ? { key: location.state.presetCategory, ts: Date.now() } : undefined
  );

  useSEO({
    title: "The Wedding Photographie | Fotografía y Video de Bodas en Querétaro",
    description: "Fotografía y video profesional de bodas, XV años y eventos sociales en Querétaro y alrededores. Cobertura elegante, paquetes personalizados y cotización inmediata por WhatsApp.",
    path: "/",
  });

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data));
    api.get("/prices").then((r) => setPrices(r.data));
  }, []);

  // Links/buttons on internal service pages (and the Navbar when clicked from
  // one of those pages) navigate to "/#section" — React Router does not
  // auto-scroll to hash fragments in an SPA, so we do it here on arrival.
  useEffect(() => {
    const id = location.hash?.replace("#", "");
    if (id) scrollToSectionId(id);
  }, [location.hash, location.key]);

  const scrollToQuote = useCallback((serviceKey) => {
    const category = SERVICE_TO_PACKAGE_CATEGORY[serviceKey] || "bodas";
    setPresetCategory({ key: category, ts: Date.now() });
    scrollToSectionId("cotizador");
  }, []);

  const onHeroQuoteClick = useCallback(() => scrollToQuote(undefined), [scrollToQuote]);
  const brandName = useMemo(() => content?.hero_headline || "The Wedding Photographie", [content?.hero_headline]);

  return (
    <div data-testid="home-page" className="bg-black min-h-screen">
      <div className="noise-overlay" />
      <Navbar logoUrl={content?.logo_url} brandName={brandName} socialInstagram={content?.social_instagram} socialFacebook={content?.social_facebook} />
      <Hero content={content} onQuoteClick={onHeroQuoteClick} />
      <div className="bg-black border-y border-white/10 py-5">
        <Marquee speed={35} gradient={false}>
          {MARQUEE_ITEMS.map((id) => (
            <span key={id} className="font-serif-display text-white/40 text-2xl mx-8">
              Bodas ✦ XV Años ✦ Bautizos ✦ Sesiones ✦ Foto + Video ✦
            </span>
          ))}
        </Marquee>
      </div>

      <Services content={content} onQuoteClick={scrollToQuote} />
      <Gallery />
      <Videos content={content} />
      <QuoteCalculator prices={prices} presetCategory={presetCategory?.key} />
      <Contact content={content} />
      <Footer brandName={brandName} logoUrl={content?.logo_url} socialInstagram={content?.social_instagram} socialFacebook={content?.social_facebook} />
      <WhatsAppFloat number={content?.whatsapp_number} />
    </div>
  );
}
